/* eslint-disable react-hooks/exhaustive-deps */
import { isEmpty } from 'lodash'
import { FC, useCallback, useEffect, useState } from 'react'
import { shallowEqual, useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router-dom'
import { toast } from 'react-toastify'
import { PrivateLoader } from 'src/components/templates'
import BackButton from 'src/components/UI/CustomUI/atoms/BackButton'
import Icon from 'src/components/UI/CustomUI/atoms/Icon'
import Modal from 'src/components/UI/CustomUI/molecules/Modal'
import SelectAddress from 'src/components/UI/CustomUI/organisms/SelectAddress'
import TabNavigation from 'src/components/UI/CustomUI/organisms/TabNavigation'

import { SelectInputOption } from 'src/components/UI/CustomUI/atoms/SelectInput/types'
import BoxContainer from 'src/components/UI/CustomUI/molecules/BoxContainer'
import {
    clientActions,
    clientPropertiesActions,
    companiesActions,
    priceModelActions
} from 'src/ducks/actions'
import {
    employeeIsAdmin,
    getAllCompaniesForMultipleFieldWithoutAllOption,
    getClient, getClientData, getClientInfo,
    getClientPriceModelFlatten, getClientStatics, getEmployeeRoles, getPriceModelAffiliate,
    getPriceModelNar
} from 'src/ducks/selectors'
import { Address, Client } from 'src/ducks/types'
import { CLIENT_TYPE, EMPLOYEE_ROLE_TYPE, PHONE_MASK_INPUT, PHONE_MASK_REGEXP } from 'src/helpers'
import { Box, Button, Grid, SelectInput, Stats, Switch, TextFieldLabel, Typography } from '../../../UI'
import { ModalActionType } from '../JobsView/ControlButtons/Modals/ModalMaterialInvoices/types'
import Estimates from './Tabs/Estimates'
import Invoices from './Tabs/Invoices'
import Jobs from './Tabs/Jobs'
import Profile from './Tabs/Profile'

const CustomerView: FC = () => {
  const { id } = useParams()
  const [pageLoading, setPageLoading] = useState(true)
  const dispatch = useDispatch()

  const priceModelOptions = useSelector(getClientPriceModelFlatten)
  const OPTIONS: SelectInputOption[] = [
    { key: 'NAR', label: 'NAR' },
    { key: 'AFFILIATE', label: 'Affiliate' },
  ]
  const CLIENT_OPTIONS: SelectInputOption[] = [
    { key: CLIENT_TYPE.HOMEOWNER, label: 'Home Owner' },
    { label: 'Institutional', key: CLIENT_TYPE.INSTITUTIONAL },
    { label: 'Broker', key: CLIENT_TYPE.BROKER },
    { label: 'Agent', key: CLIENT_TYPE.AGENT },
    { label: 'Inspector', key: CLIENT_TYPE.INSPECTOR },
  ]
  const affiliateOptions: SelectInputOption[] = useSelector(
    getAllCompaniesForMultipleFieldWithoutAllOption
  )
  const [open, setOpen] = useState(false)

  const client = useSelector(getClient())
  const narPrice = useSelector(getPriceModelNar)
  const affiliatePrice = useSelector(getPriceModelAffiliate)
  const narData = useSelector(
    getClientInfo(narPrice, affiliatePrice),
    shallowEqual
  )
  const userData = useSelector(getClientData())
  const userStatistics = useSelector(getClientStatics())
  const isAdmin = useSelector(employeeIsAdmin)
  const employeeRoles = useSelector(getEmployeeRoles)
  const employeeIsManager = employeeRoles.some((role) => role.roleType === EMPLOYEE_ROLE_TYPE.MANAGER)

  // States to edit contact information
  const [openContactModal, setOpenContactModal] = useState(false)
  const [newContact, setNewContact] = useState(client)
  const [showWarnings, setShowWarnings] = useState(false)
  const [loading, setLoading] = useState(false)
  // States to edit address
  const [openAddressModal, setOpenAddressModal] = useState(false)
  const [address, setAddress] = useState<any>('')

  const narInitial = narData.nar.value === 'Yes'
  const discountInitial = {
    key: narData.discount.value,
    label: narData.discount.value,
  }
  const revInitial = {
    key: narData.revShare.value,
    label: narData.revShare.value,
  }

  const affInitial = !client.affiliate
    ? null
    : affiliateOptions.find((affiliate) => affiliate.key === client.affiliate)

  const [customer, setCustomer] = useState({
    nar: narInitial,
    discount: discountInitial,
    rev: revInitial,
    aff: affInitial,
    type: CLIENT_OPTIONS.find((option) => option.key === client.clientType),
  })

  const { nar, discount, rev, aff, type } = customer

  const { firstName, lastName, phone, email } = newContact

  // Validation of contact
  const validate =
    !isEmpty(firstName) &&
    !isEmpty(lastName) &&
    ((PHONE_MASK_REGEXP.test(phone?.toString()) && !isEmpty(phone)) || isEmpty(phone))

  // Validation of address
  const isValidAddress: boolean =
    !isEmpty(address?.line1) &&
    address?.line1 !== ' ' &&
    !!address?.zipCode &&
    address?.latitude &&
    address?.longitude

  useEffect(() => {
    resetCustomer()
    // setType(CLIENT_OPTIONS.find((option) => option.key === client.clientType))
  }, [client])

  useEffect(() => {
    setNewContact(client)
  }, [client])

  const hasChange =
    nar !== narInitial ||
    discount !== discountInitial ||
    rev !== revInitial ||
    aff !== affInitial ||
    type?.key !== client.type

  const fetchClientPriceModels = useCallback(() => {
    dispatch(
      priceModelActions.fetchClientPriceModels((_succ: boolean) => {
        setPageLoading(false)
      })
    )

    dispatch(companiesActions.fetchCompanies({}, (succ: boolean) => { }))
  }, [])

  const fetchClient = useCallback(() => {
    id &&
      dispatch(
        clientActions.fetchClient(id, (_succ: boolean) => {
          fetchClientPriceModels()
        })
      )
  }, [])

  const fetchClientProperties = useCallback(() => {
    id &&
      dispatch(
        clientPropertiesActions.fetchClientProperties(id, (_succ: boolean) => { })
      )
  }, [])

  useEffect(() => {
    fetchClient()
    fetchClientProperties()
  }, [])

  const handleCloseContact = () => {
    setOpenContactModal(false)
    setShowWarnings(false)
    setNewContact(client)
  }

  const handleCloseAddress = () => {
    setOpenAddressModal(false)
    setAddress('')
  }

  const handleUpdateClient = (
    contact: Partial<Client>,
  ) => {
    if (!client.id || isEmpty(contact)) return;
    dispatch(
      clientActions.updateClientId(client.id, contact, (succ: boolean) => {
        if (succ) {
          toast.success("Customer was updated successfully")
          setOpenContactModal(false)
          setOpenAddressModal(false)
          fetchClient()
        } else {
          setNewContact(client)
        }
      })
    )
  }

  const getChangedContactFields = (
    contact: Partial<Client>,
    newContact: Partial<Client>
  ) => {
    const edited = { ...newContact }
    if (edited.phone && typeof edited.phone === "string") {
      edited.phone = Number(edited.phone.replaceAll('-', ''))
    }
    for (const attr in newContact) {
      if (
        contact &&
        newContact[attr as keyof Client] ===
        contact[attr as keyof Client]
      ) {
        delete edited[attr as keyof Client]
      }
    }
    return edited
  }

  const handleSaveContact = () => {
    setLoading(true)
    if (validate) {
      client.fullName = `${firstName} ${lastName}`
      setNewContact({ ...newContact })
      handleUpdateClient(
        getChangedContactFields(client, newContact)
      )
    } else {
      setShowWarnings(true)
    }
    setLoading(false)
  }

  const handleChangeAddress = (newAddress: Address) => {
    setAddress(newAddress)
  }

  const handleSaveAddress = () => {
    if (isValidAddress) {
      const editAddress: Address = {
        city: address.city,
        state: address.state,
        line1: address.line1,
        line2: address.line2,
        zipCode: address.zipCode,
        latitude: address.latitude,
        longitude: address.longitude,
        fullAddress: ''
      }
      handleUpdateClient(
        getChangedContactFields(client, { address: editAddress })
      )
    }
  }

  const handlePhone = (e: any) => {
    setNewContact({ ...newContact, phone: e.target.value })
  }

  const handleEmail = (e: any) => {
    setNewContact({ ...newContact, email: [{ ...newContact.email?.[0] as any, email: e.target.value }] })
  }

  const resetCustomer = () => {
    setCustomer({
      nar: narInitial,
      discount: discountInitial,
      rev: revInitial,
      aff: affInitial,
      type: CLIENT_OPTIONS.find((option) => option.key === client.clientType),
    })
  }

  const handleOpenModal = () => {
    resetCustomer()
    setOpen(true)
  }

  const TABS = [
    {
      label: 'Profile',
      content: <Profile openModal={handleOpenModal} />,
    },
    {
      label: 'Jobs',
      content: <Jobs />,
    },
    {
      label: 'Estimates',
      content: <Estimates />,
    },
    {
      label: 'Invoices',
      content: <Invoices />,
    },
  ]

  const handleEdit = () => {
    const params: any = {}

    if (nar !== narInitial) {
      params.nar = nar
    }

    if (discount !== discountInitial) {
      const discountId =
        discount.label.toLowerCase() === priceModelOptions[0].label.toLowerCase()
          ? priceModelOptions[0].key
          : priceModelOptions[1].key
      params.clientPriceModelId = discountId
    }

    if (rev !== revInitial && rev.key && rev.key !== '-') {
      params.revSharePriority = rev.key
    }

    if (aff !== affInitial) {
      if (type?.key === CLIENT_TYPE.HOMEOWNER) {
        params.affiliate = ''
      } else if (aff?.key) {
        params.affiliate = aff.key
      }
    }

    if (type?.key !== client.type) {
      params.clientType = type?.key as unknown as CLIENT_TYPE
    }

    id &&
      dispatch(
        clientActions.updateClientId(id, params, (_succ: boolean) => {
          setOpen(false)
          fetchClient()
        })
      )
  }

  const modalAction: ModalActionType[] = [
    {
      textButton: 'Cancel',
      variant: 'containedError',
      onClick: () => {
        setOpen(false)
      },
    },
    {
      textButton: 'Save',
      variant: 'contained',
      onClick: () => {
        handleEdit()
      },
      disabled: !hasChange,
    },
  ]

  // Actions of contact modal
  const modalContactActions: ModalActionType[] = [
    {
      textButton: 'Cancel',
      variant: 'outlined',
      onClick: handleCloseContact,
    },
    {
      textButton: 'Update Contact',
      variant: 'contained',
      onClick: handleSaveContact,
      icon: 'Save',
      iconColor: 'white',
      loading: loading,
    },
  ]

  // Actions of address modal
  const modalAddressActions: ModalActionType[] = [
    {
      textButton: 'Cancel',
      variant: 'outlined',
      onClick: handleCloseAddress,
    },
    {
      textButton: 'Update Address',
      variant: 'contained',
      onClick: handleSaveAddress,
      icon: 'Save',
      iconColor: 'white',
      disabled: !isValidAddress,
    },
  ]

  const emailValue = typeof userData?.email === 'string' ? userData?.email : userData?.email?.[0]?.email || ''
  const contactEmailValue = typeof email === 'string' ? email : email?.[0]?.email || ''

  return (
    <PrivateLoader loading={pageLoading}>
      <Box>
        <Grid container justifyContent="space-between" marginBottom={3}>
          <Grid item width="fit-content">
            <BackButton />
          </Grid>
        </Grid>

        {/* Contact Info and Statistics Cards */}
        <Grid container spacing={2} marginBottom={3}>
          <Grid item xs={12} md={6}>
            <BoxContainer
              sx={{ width: '100%', height: '100%', justifyContent: 'flex-start', alignItems: 'flex-start' }}
              title={
                <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                  <Typography variant="h5" fontFamily="var(--font-medium)">
                    Contact Info
                  </Typography>
                  <Button
                    variant='containedLight'
                    color='primary'
                    size="small"
                    onClick={() => setOpenContactModal(true)}
                  >
                    Edit Info
                    <Icon name="Edit" sx={{ marginLeft: '.5rem' }} />
                  </Button>
                </Box>
              }
            >
              <Box width="100%" paddingTop={2}>
                <Typography variant="h6" color="GrayText">
                  Name:
                </Typography>
                <Typography variant="h5" marginBottom={1}>
                  {`${client.firstName} ${client.lastName}`}
                </Typography>

                <Typography variant="h6" color="GrayText">
                  Phone:
                </Typography>
                <Typography variant="h5" marginBottom={1}>
                  {client.phone || '-'}
                </Typography>

                <Typography variant="h6" color="GrayText">
                  Email:
                </Typography>
                <Typography variant="h5" marginBottom={1}>
                  {emailValue || '-'}
                </Typography>

                {(isAdmin || employeeIsManager) && (
                  <>
                    <Box display="flex" justifyContent="space-between" alignItems="center" marginTop={2}>
                      <Typography variant="h6" color="GrayText">
                        Address:
                      </Typography>
                      <Button
                        variant='containedLight'
                        color='primary'
                        size="small"
                        onClick={() => setOpenAddressModal(true)}
                      >
                        Edit Address
                        <Icon name="Edit" sx={{ marginLeft: '.5rem' }} />
                      </Button>
                    </Box>
                    <Typography variant="h5" marginTop={0.5}>
                      {client.address?.line1
                        ? `${client.address.line1}${client.address.line2 ? ', ' + client.address.line2 : ''}`
                        : '-'
                      }
                    </Typography>
                    {client.address?.city && (
                      <Typography variant="h5">
                        {`${client.address.city}, ${client.address.state} ${client.address.zipCode}`}
                      </Typography>
                    )}
                  </>
                )}
              </Box>
            </BoxContainer>
          </Grid>

          <Grid item xs={12} md={6}>
            <BoxContainer
              sx={{ width: '100%', height: '100%', justifyContent: 'flex-start', alignItems: 'flex-start' }}
              title={
                <Typography variant="h5" fontFamily="var(--font-medium)">
                  Statistics
                </Typography>
              }
            >
              <Box width="100%" >
                <Stats data={userStatistics} />
              </Box>
            </BoxContainer>
          </Grid>
        </Grid>

        <TabNavigation tabs={TABS} variant="text" />

        {/* Modal to edit contact information */}
        {openContactModal && (
          <Modal
            setOpen={setOpenContactModal}
            open={openContactModal}
            title={'Edit Customer Information'}
            actions={modalContactActions}
            onClose={handleCloseContact}
          >
            <Grid container spacing={1}>
              <Grid item xs={6}>
                <TextFieldLabel
                  name="edit-customer-firstname"
                  id="edit-customer-firstname"
                  type="text"
                  labelVariant="h6"
                  label="First Name:"
                  value={firstName}
                  error={showWarnings && isEmpty(firstName)}
                  onChange={(e) => {
                    setNewContact({ ...newContact, firstName: e.target.value })
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextFieldLabel
                  name="edit-customer-lastname"
                  id="edit-customer-lastname"
                  type="text"
                  labelVariant="h6"
                  label="Last Name:"
                  value={lastName}
                  error={showWarnings && isEmpty(lastName)}
                  onChange={(e) => {
                    setNewContact({ ...newContact, lastName: e.target.value })
                  }}
                />
              </Grid>
              <Grid item xs={6}>
                <TextFieldLabel
                  name="edit-customer-phone"
                  id="edit-customer-phone"
                  labelVariant="h6"
                  label="Phone:"
                  value={phone?.toString()}
                  placeholder="876-543-1223"
                  mask={PHONE_MASK_INPUT}
                  error={
                    showWarnings &&
                    !isEmpty(phone) &&
                    !PHONE_MASK_REGEXP.test(phone?.toString())
                  }
                  onChange={handlePhone}
                />
              </Grid>
              <Grid item xs={6}>
                <TextFieldLabel
                  name="edit-customer-email"
                  id="edit-customer-email"
                  labelVariant="h6"
                  label="Email:"
                  value={contactEmailValue}
                  placeholder="example@email.com"
                  onChange={handleEmail}
                />
              </Grid>
            </Grid>
          </Modal>
        )}

        {/* Modal to edit address */}
        {openAddressModal && (
          <Modal
            setOpen={setOpenAddressModal}
            open={openAddressModal}
            title={'Edit Address Information'}
            actions={modalAddressActions}
            onClose={handleCloseAddress}
          >
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <SelectAddress
                  placeholder="Address"
                  showWarnings={false}
                  savedAddress={address}
                  onChange={handleChangeAddress}
                />
              </Grid>
            </Grid>
          </Modal>
        )}

        <Box
          sx={{
            position: 'absolute',
            bottom: '12px',
            right: '12px',
          }}
        >
          {open && (
            <Modal setOpen={setOpen} open={open} title="" actions={modalAction}>
              <Grid
                container
                spacing={1}
                justifyContent="center"
                alignItems="center"
              >
                <Grid item xs={12}>
                  <Typography variant="h4">Edit information: </Typography>
                </Grid>

                <Grid container item xs={12} spacing={1}>
                  <Grid item xs={10} alignSelf="center">
                    <Typography variant="h6">
                      {narData.nar.label + ':'}
                    </Typography>
                  </Grid>
                  <Grid item xs={2} alignSelf="end">
                    <Switch
                      checked={nar}
                      onChange={() => {
                        setCustomer({ ...customer, nar: !nar })
                      }}
                      inputProps={{ 'aria-label': 'controlled' }}
                    />
                  </Grid>
                </Grid>
                <Grid item xs={12}>
                  <SelectInput
                    label="Type:"
                    options={CLIENT_OPTIONS}
                    placeholder=""
                    onChange={(newSelected: SelectInputOption) => {
                      setCustomer({ ...customer, type: newSelected })
                    }}
                    value={type}
                    disableClearable
                    canRemove={false}
                  />
                </Grid>
                <Grid item xs={12}>
                  <SelectInput
                    label={narData.discount.label + ':'}
                    options={priceModelOptions}
                    placeholder=""
                    onChange={(newSelected: SelectInputOption) => {
                      setCustomer({ ...customer, discount: newSelected })
                    }}

                    value={discount}
                    size="small"
                  />
                </Grid>

                <Grid item xs={12}>
                  <SelectInput
                    label={narData.revShare.label + ':'}
                    options={OPTIONS}
                    placeholder=""
                    onChange={(newSelected: SelectInputOption) => {
                      setCustomer({ ...customer, rev: newSelected })
                    }}
                    value={rev}
                    size="small"
                  />
                </Grid>

                {type?.key !== CLIENT_TYPE.HOMEOWNER && (
                  <Grid item xs={12}>
                    <SelectInput
                      label={narData.affiliate.label + ':'}
                      options={affiliateOptions}
                      placeholder=""
                      onChange={(newSelected: SelectInputOption) => {
                        setCustomer({ ...customer, aff: newSelected })
                      }}
                      value={aff}
                      size="small"
                    />
                  </Grid>
                )}
              </Grid>
            </Modal>
          )}
        </Box>
      </Box>
    </PrivateLoader>
  )
}

export default CustomerView
